import type { RouteMiddleware } from 'nuxt/app';
import { Tracer } from '~~/lib/Tracing';
import { createNewUrlFromRedirectAndCurrentUrl } from '~/utils/createNewUrlFromRedirectAndCurrentUrl';
import { getPlainUrl } from '~/utils/getPlainUrl';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import { enhancedNavigateTo } from '~/utils/enhancedNavigateTo';
import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { resolveContentfulPageRouteMode } from '~/utils/contentful/resolveContentfulPageRouteMode';
import { isMagentoShopPath } from '~/utils/shop/isMagentoShopPath';
import { shouldSkipMiddleware } from '~/utils/routing/routeExclusions';

/**
 * Client-side cache for non-CMS routes that still use `/api/contentful/redirect`.
 */
const redirectCache = new Map<
  string,
  {
    result: { statusCode?: number; url?: URL };
    timestamp: number;
    ttl: number;
  }
>();

const CLIENT_CACHE_TTL = 2 * 60 * 1000;
const NO_REDIRECT_TTL = 10 * 60 * 1000;
const MAX_CACHE_SIZE = 200;

function getCachedRedirect(
  path: string
): { statusCode?: number; url?: URL } | null {
  const cached = redirectCache.get(path);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.result;
  }

  if (cached) {
    redirectCache.delete(path);
  }

  return null;
}

function setCachedRedirect(
  path: string,
  result: { statusCode?: number; url?: URL }
): void {
  if (redirectCache.size >= MAX_CACHE_SIZE) {
    const oldestKeys = Array.from(redirectCache.keys()).slice(
      0,
      Math.floor(MAX_CACHE_SIZE / 3)
    );
    oldestKeys.forEach((key) => redirectCache.delete(key));
  }

  const ttl = result.url ? CLIENT_CACHE_TTL : NO_REDIRECT_TTL;

  redirectCache.set(path, {
    result,
    timestamp: Date.now(),
    ttl
  });
}

function getCacheStats(): { size: number; maxSize: number; hitRate?: number } {
  return {
    size: redirectCache.size,
    maxSize: MAX_CACHE_SIZE
  };
}

function clearRedirectCache(): void {
  redirectCache.clear();
}

/**
 * Contentful redirects for CMS routes are resolved in `/api/contentful`.
 * Shop redirects come from Magento (`redirect_code` on `/api/magento/product`).
 * This middleware only handles remaining non-CMS, non-shop paths.
 */
const pageRedirectMiddleware: RouteMiddleware = async (to) => {
  const tracer = new Tracer('middleware');

  if (shouldSkipMiddleware(to.path)) {
    return;
  }

  if (
    resolveContentfulPageRouteMode(to) !== false ||
    isMagentoShopPath(to.path)
  ) {
    return;
  }

  await tracer.withSpan('pageRedirect', {}, async () => {
    if (import.meta.server) {
      return;
    }

    const nuxtApp = useNuxtApp();
    if (
      import.meta.client &&
      nuxtApp.isHydrating &&
      nuxtApp.payload.serverRendered
    ) {
      return;
    }

    const url = useRequestURL();
    const cacheKey = getPlainUrl(url);

    let redirect = getCachedRedirect(cacheKey);

    if (!redirect) {
      redirect = await getRedirectUrl(to.path, url);
      setCachedRedirect(cacheKey, redirect);
    }

    if (!redirect.url) {
      return;
    }

    if (redirect.url.pathname === to.path) {
      useLogger().warn(
        `[PageRedirect] Prevented endless redirect loop for path: ${to.path}`
      );
      return;
    }

    return enhancedNavigateTo(
      {
        query: Object.fromEntries(redirect.url.searchParams.entries()),
        force: true,
        path: redirect.url.href
      },
      {
        redirectCode: redirect.statusCode,
        external: url.origin !== redirect.url.origin
      }
    );
  });
};

async function getRedirectUrl(
  path: string,
  currentUrl: URL
): Promise<{ statusCode?: number; url?: URL }> {
  try {
    const pageRedirect = await $fetch('/api/contentful/redirect', {
      ...DEFAULT_FETCH_OPTIONS,
      query: { path }
    });

    if (!pageRedirect?.redirect) {
      return {};
    }

    const store = useGeoinformationStore();

    const redirectUrl = await createNewUrlFromRedirectAndCurrentUrl(
      pageRedirect.redirect,
      currentUrl,
      () => store.ensureRedirectLocale()
    );

    return {
      statusCode: pageRedirect.redirect.statusCode,
      url: redirectUrl
    };
  } catch (error) {
    useLogger().error(
      '[PageRedirect] Failed to fetch redirect configuration',
      error
    );

    return {};
  }
}

export { getCacheStats, clearRedirectCache };

export default defineNuxtRouteMiddleware(pageRedirectMiddleware);
